import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  IDENTITY_FEATURE_FLAG_DESCRIPTION,
  IDENTITY_FEATURE_FLAG_KEY,
} from '../constants/identity-feature-flag-keys.constants';

interface FeatureFlagRow {
  key: string;
  enabled: boolean;
  description: string;
}

export interface IdentityAdminOverview {
  layerEnabled: boolean;
  legacyGraceDays: number;
  kycProvider: string;
  fullyVerifiedRequiresCourse: boolean;
}

export interface IdentityGraceBackfillResult {
  assignedCount: number;
}

@Injectable({ providedIn: 'root' })
export class IdentityLayerAdminService {
  #http = inject(HttpClient);

  async getOverview(): Promise<IdentityAdminOverview> {
    const [overview, courseFlag] = await Promise.all([
      lastValueFrom(
        this.#http
          .get<Omit<IdentityAdminOverview, 'fullyVerifiedRequiresCourse'>>(
            `${environment.baseUrl}/user/identity/admin/overview`,
          )
          .pipe(take(1)),
      ),
      this.#getFlag(IDENTITY_FEATURE_FLAG_KEY.FULLY_VERIFIED_REQUIRES_COURSE),
    ]);

    return {
      ...overview,
      fullyVerifiedRequiresCourse: courseFlag.enabled,
    };
  }

  async isLayerEnabled(): Promise<boolean> {
    const flag = await this.#getFlag(IDENTITY_FEATURE_FLAG_KEY.LAYER);
    return flag.enabled;
  }

  async setLayerEnabled(enabled: boolean): Promise<void> {
    await this.#upsertFlag(IDENTITY_FEATURE_FLAG_KEY.LAYER, enabled);
  }

  async setFullyVerifiedRequiresCourseEnabled(enabled: boolean): Promise<void> {
    await this.#upsertFlag(
      IDENTITY_FEATURE_FLAG_KEY.FULLY_VERIFIED_REQUIRES_COURSE,
      enabled,
    );
  }

  async backfillLegacyGrace(): Promise<IdentityGraceBackfillResult> {
    return lastValueFrom(
      this.#http
        .post<IdentityGraceBackfillResult>(
          `${environment.baseUrl}/user/identity/admin/grace/backfill`,
          {},
        )
        .pipe(take(1)),
    );
  }

  async #getFlag(key: string): Promise<FeatureFlagRow> {
    return lastValueFrom(
      this.#http
        .get<FeatureFlagRow>(
          `${environment.baseUrl}/user/feature-flags/${encodeURIComponent(key)}`,
        )
        .pipe(take(1)),
    );
  }

  async #upsertFlag(key: string, enabled: boolean): Promise<void> {
    await lastValueFrom(
      this.#http
        .post(`${environment.baseUrl}/user/feature-flags`, {
          key,
          enabled,
          description:
            IDENTITY_FEATURE_FLAG_DESCRIPTION[
              key as keyof typeof IDENTITY_FEATURE_FLAG_DESCRIPTION
            ] ?? '',
        })
        .pipe(take(1)),
    );
  }
}
